import React, { useCallback } from 'react';
import { Box } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import GlobalPromptQuestionEditCard from './GlobalPromptQuestionEditCard';
import type { Control } from 'react-hook-form';
import type { FormValues } from '../hooks/validationSchema';
import { useAutoScrollToBottom } from '@/hooks';

interface SortableGlobalPromptItemProps {
    fieldKey: string;
    index: number;
    control: Control<FormValues>;
    isFirst: boolean;
    isLast: boolean;
    isSortingDisabled: boolean;
    highlightedItemId: string | null;
    onDelete: () => void;
    onReorder: (from: number, to: number) => void;
}

const SortableGlobalPromptItem: React.FC<SortableGlobalPromptItemProps> = ({
    fieldKey,
    index,
    control,
    isFirst,
    isLast,
    isSortingDisabled,
    highlightedItemId,
    onDelete,
    onReorder,
}) => {
    const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: fieldKey,
        disabled: isSortingDisabled,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const handleMoveUp = useCallback(() => {
        if (!isFirst) onReorder(index, index - 1);
    }, [isFirst, onReorder, index]);

    const handleMoveDown = useCallback(() => {
        if (!isLast) onReorder(index, index + 1);
    }, [isLast, onReorder, index]);

    const isHighlighted = highlightedItemId === fieldKey;
    const setRefs = useAutoScrollToBottom<HTMLDivElement>(isHighlighted, setNodeRef);

    return (
        <Box ref={setRefs} style={style}>
            <GlobalPromptQuestionEditCard
                fieldKey={fieldKey}
                index={index}
                control={control}
                displayNumber={index + 1}
                isFirst={isFirst}
                isLast={isLast}
                dragHandleProps={isSortingDisabled ? undefined : listeners}
                onDelete={onDelete}
                onMoveUp={isSortingDisabled ? undefined : handleMoveUp}
                onMoveDown={isSortingDisabled ? undefined : handleMoveDown}
                isHighlighted={isHighlighted}
            />
        </Box>
    );
};

export default SortableGlobalPromptItem;
